import {SharedColors} from "@fluentui/theme";
import {Icon, ILinkProps, INavLinkGroup, Stack, StackItem, Text,} from "@fluentui/react";
import {
    FC,
    HTMLAttributes,
    PropsWithChildren,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import Color from "color";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {OnixRoleEnum} from "../enums/OnixRoleEnum";
import {DocumentService} from "./DocumentService";
import {AppRoute} from "../AppRoutes";
import {UserInfoHelper} from "./UserInfoHelper";
import {PORTAL_HOME} from "./PortalSignInHelper";
import grunfImage from "../images/Grunf.png";
import number1Image from "../images/Number1.png";
import onixDesktopImage from "../images/onix-samo-logo-veliko.png";
import bob from "../images/Bob.png";
import {theme} from "../index";
import {hr} from "../locales/hr";
import "../styles/ApplicationListHelper.css";
import {MakeHub} from "../MakeHub";
import {
    ApplicationPacketEnum,
    DocumentFunctionalityEnum,
    DocumentPairFunctionalityEnum,
    UserQuery,
} from "../graphql-types";
import {
    getMainMenuGroupItemFeatureIcon,
    MainMenuGroup,
    MainMenuItem,
    MainMenuItemFeature,
    MainMenuGroupType,
    makeMainMenuGroups,
    shownWebDocumentFeatures,
} from "./makeWebDocumentsGroups";
import {AuthContext} from "../contexts/AuthContext";

export interface AlternativeBottomIconDefinition {
    name: keyof typeof hr;
    icon?: string;
    url: string;
    color?: string;
    image?: any;
}

export interface SubApplicationDefinition {
    name: keyof typeof hr | string;
    icon?: string;
    url: string;
    color?: string;
    image?: any;
    alternativeBottomIcon?: AlternativeBottomIconDefinition;
    wrapper?: ({
                   children,
                   key,
               }: {
        children: ReactElement;
        key: string;
    }) => ReactElement;
    customElement?: ReactElement;
    openInNewTab?: boolean;
}

export interface ApplicationDefinition extends SubApplicationDefinition {
    links?: SubApplicationDefinition[];
}

export interface ApplicationDefinitionGroup {
    applications: ApplicationDefinition[];
    name: string;
    color?: string;
}

export class ApplicationGroups {
    groups: ApplicationDefinitionGroup[] = [];
    ToNavGroups: (IsDeleted: boolean) => INavLinkGroup[] = (IsDeleted: boolean) =>
        this.groups.map((group) => ({
            name: group.name,
            links: group.applications.map((app) => ({
                disabled: IsDeleted,
                iconProps: app.icon
                    ? {
                        iconName: app.icon,
                        color: group.color,
                        style: {
                            color: group.color,
                        },
                    }
                    : undefined,
                key: app.url,
                name: app.name,
                url: app.url,
                links: app.links?.map((subApp) => ({
                    disabled: IsDeleted,
                    isExpanded: true,
                    iconProps: {
                        iconName: subApp.icon,
                        color: group.color,
                        style: {
                            color: group.color,
                        },
                    },
                    key: subApp.url,
                    name: subApp.name,
                    url: subApp.url,
                })),
                isExpanded: true,
            })),
        }));
    FlattenLinks: () => ApplicationDefinitionGroup[] = () =>
        this.groups.map((g) => {
            g.applications = g.applications.reduce<ApplicationDefinition[]>(
                (prev, curr) => {
                    if (curr.links) {
                        curr.links.forEach((v) => prev.push(v));
                    } else {
                        prev.push(curr);
                    }
                    return prev;
                },
                [],
            );
            return g;
        });
}

function makeDeveloperGroups(
    userInfo: UserQuery | undefined,
    groups: ApplicationDefinitionGroup[],
    isOnixInfo: boolean,
) {
    if (UserInfoHelper.HasRole(OnixRoleEnum.DEVELOPER, userInfo)) {
        const groupColor = SharedColors.gray40;

        const apps: ApplicationDefinition[] = [
            {
                name: "Hangfire",
                url: AppRoute.HANGFIRE_DASHBOARD,
                icon: "DatabaseSync",
                color: groupColor,
            },
            {
                name: "caches",
                url: AppRoute.CACHES,
                icon: "StackedBarChart",
                color: groupColor,
            },
        ];

        groups.push({
            name: "program",
            color: groupColor,
            applications: apps,
        });
    }
}

function makeAdminGroups(
    userInfo: UserQuery | undefined,
    groups: ApplicationDefinitionGroup[],
    isOnixInfo: boolean,
) {
    if (UserInfoHelper.HasRole(OnixRoleEnum.ADMIN, userInfo)) {
        const groupColor = SharedColors.red20;

        groups.push({
            name: "administration",
            color: groupColor,
            applications: [
                {
                    name: "users",
                    url: AppRoute.ADMIN_USERS,
                    icon: "ContactList",
                    color: groupColor,
                    wrapper: ({children, key}) => (
                        <ActiveUsersWrapper key={key}>{children}</ActiveUsersWrapper>
                    ),
                },
                {
                    name: "databases",
                    url: AppRoute.ADMIN_COMPANIES,
                    icon: "LinkedDatabase",
                    color: groupColor,
                },

                // Pantheon docs (Support)
                {
                    name: "pantheon_docs",
                    url: AppRoute.PANTHEON_DOCS,
                    icon: "TextDocument",
                    color: groupColor,
                },
                {
                    name: "Datalab mappings",
                    url: AppRoute.DATALAB_MAPPINGS,
                    icon: "Relationship",
                    color: groupColor,
                },
                {
                    name: "AI Knowledge",
                    url: AppRoute.AI_KNOWLEDGE,
                    icon: "KnowledgeArticle",
                    color: groupColor,
                },
                {
                    name: "label_definitions",
                    url: AppRoute.LABEL_DEFINITIONS,
                    icon: "Tag",
                },
                {
                    name: "onix_phone_user_tasks",
                    url: AppRoute.ONIX_PHONE_USER_TASKS,
                    icon: "TaskManager",
                },
                {
                    name: "fiscalizations",
                    url: AppRoute.FISCALIZATIONS,
                    icon: "Money",
                },
                {
                    name: "clientServers",
                    url: AppRoute.CLIENT_SERVERS,
                    icon: "ServerEnviroment",
                },
                // Ares is for Onix support staff only (User role + Support packet), as on the server.
                ...(UserInfoHelper.HasRole(OnixRoleEnum.USER, userInfo) &&
                UserInfoHelper.HasPacket(userInfo, ApplicationPacketEnum.Support)
                    ? [
                        {
                            name: "aresDeployments",
                            url: AppRoute.ARES_DEPLOYMENTS,
                            icon: "CloudUpload",
                        },
                        {
                            name: "aresActivityReport",
                            url: AppRoute.ARES_ACTIVITY_REPORT,
                            icon: "BarChartVertical",
                        },
                    ]
                    : []),
                {
                    name: "onixPhoneFunctionalityActivityReport",
                    url: AppRoute.ONIX_PHONE_FUNCTIONALITY_ACTIVITY_REPORT,
                    icon: "BarChartVertical",
                }
                ,
                {
                    name: "onixDmsBillingReport",
                    url: AppRoute.ONIX_DMS_BILLING_REPORT,
                    icon: "ExcelDocument",
                }
                ,
                {
                    name: "document_type_templates",
                    url: AppRoute.ADMIN_DOCUMENT_TYPE_TEMPLATES,
                    icon: "PasteAsText",
                }
            ],
        });
    }
}

function makeTenantGroups(
    userInfo: UserQuery | undefined,
    groups: ApplicationDefinitionGroup[],
    isOnixInfo: boolean,
) {
    if (UserInfoHelper.HasRole(OnixRoleEnum.TENANT, userInfo)) {
        const groupColor = SharedColors.cyanBlue20;

        const tenantApps: ApplicationDefinition[] = [
            {
                name: "workers",
                url: AppRoute.COMPANY_USERS,
                icon: "RecruitmentManagement",
                color: groupColor,
            },
            {
                name: "settings",
                url: AppRoute.COMPANY_SETTINGS,
                icon: "settings",
                color: groupColor,
            },
        ];

        if (UserInfoHelper.HasPacket(userInfo, ApplicationPacketEnum.Support)) {
            tenantApps.push({
                name: "support",
                url: AppRoute.SUPPORT_TIME_LOGS,
                color: groupColor,
                links: [
                    {
                        name: "support_time_logs",
                        color: groupColor,
                        url: AppRoute.SUPPORT_TIME_LOGS,
                        icon: "Timer",
                    },
                    {
                        name: "prepared_time_logs",
                        color: groupColor,
                        url: AppRoute.PREPARED_SUPPORT_TIME_LOGS,
                        icon: "AutoEnhanceOff",
                    },
                    {
                        name: "support_absences",
                        color: groupColor,
                        url: AppRoute.SUPPORT_ABSENCES,
                        icon: "Calendar",
                    },
                    {
                        name: "support_todos",
                        color: groupColor,
                        url: AppRoute.SUPPORT_TODOS,
                        icon: "ReminderTime",
                    },
                    {
                        name: "support_tasks",
                        color: groupColor,
                        url: AppRoute.SUPPORT_TASKS,
                        icon: "CheckList",
                    },
                    {
                        name: "Bob",
                        color: groupColor,
                        url: AppRoute.SUPPORT_BOB,
                        image: bob,
                    },
                    {
                        name: "team_viewer_sessions",
                        color: groupColor,
                        url: AppRoute.TEAM_VIEWER_SESSIONS,
                        icon: "Remote",
                    },
                    {
                        name: "WakaTime",
                        color: groupColor,
                        url: AppRoute.WAKA_TIME_WORKLOGS,
                        icon: "Code",
                    },
                    {
                        name: "attachments",
                        color: groupColor,
                        url: AppRoute.SAVED_FILES,
                        icon: "Attach",
                    },
                    {
                        name: "pantheon_inis",
                        color: groupColor,
                        url: AppRoute.PANTHEON_INIS,
                        icon: "FileCode",
                    },
                    {
                        name: "codebooks-time-log-templates",
                        url: AppRoute.CODEBOOKS_TIME_LOG_TEMPLATES,
                        icon: "Timer",
                    },
                    {
                        name: "email-settings-testing",
                        url: AppRoute.EMAIL_SETTINGS_TESTING,
                        icon: "Mail",
                    },
                    {
                        name: "pantheon-upgrade-logs",
                        url: AppRoute.PANTHEON_UPGRADE_LOGS,
                        icon: "UpgradeAnalysis",
                    },
                    {
                        name: "onix-clr-installations",
                        url: AppRoute.ONIX_CLR_INSTALLATIONS,
                        icon: "Database",
                    },
                    {
                        name: "onixClrUsageReport",
                        url: AppRoute.ONIX_CLR_USAGE_REPORT,
                        icon: "BarChartVertical",
                    },
                    {
                        name: "onixDmsBillingReport",
                        url: AppRoute.ONIX_DMS_BILLING_REPORT,
                        icon: "ExcelDocument",
                    },
                    {
                        name: "onixPhoneCompaniesOverview",
                        url: AppRoute.ONIX_PHONE_COMPANIES_OVERVIEW,
                        icon: "CompanyDirectory",
                    },
                    {
                        name: "canned-responses",
                        color: groupColor,
                        url: AppRoute.SUPPORT_CANNED_RESPONSES,
                        icon: "QuickNote",
                    },
                    {
                        name: "support-groups",
                        color: groupColor,
                        url: AppRoute.SUPPORT_GROUPS,
                        icon: "Group",
                    }
                ],
            });
        }

        if (DocumentService.HasWorkRecord(userInfo)) {
            tenantApps.push({
                name: "work_records",
                color: groupColor,
                url: AppRoute.WORK_RECORDS_OVERVIEW(),
                links: [
                    {
                        name: "overview",
                        color: groupColor,
                        icon: "CalendarSettings",
                        url: AppRoute.WORK_RECORDS_OVERVIEW(),
                    },
                    {
                        name: "finalizing",
                        color: groupColor,
                        url: AppRoute.WORK_RECORDS_FINALIZE,
                        icon: "PageLock",
                    },
                    {
                        name: "transfer",
                        color: groupColor,
                        url: AppRoute.WORK_RECORDS_UPLOAD,
                        icon: "BulkUpload",
                    },
                ],
            });
        }

        if (
            UserInfoHelper.HasPacket(
                userInfo,
                ApplicationPacketEnum.MarservisWorkOrders,
            )
        ) {
            tenantApps.push({
                name: "work_orders",
                color: groupColor,
                url: AppRoute.MARSERVIS_WORK_ORDERS,
                links: [
                    {
                        name: "work_orders",
                        url: AppRoute.MARSERVIS_WORK_ORDERS,
                        icon: "Work",
                    },
                    {
                        name: "work_orders_overview",
                        url: AppRoute.MARSERVIS_WORK_ORDERS_OVERVIEW,
                        icon: "AnalyticsView",
                    },
                ],
            });
        }

        groups.push({
            name: "company",
            color: groupColor,
            applications: tenantApps,
        });
    }
}

function makeUserGroups(
    userInfo: UserQuery | undefined,
    groups: ApplicationDefinitionGroup[],
    isOnixInfo: boolean,
) {
    if (UserInfoHelper.HasRole(OnixRoleEnum.USER, userInfo)) {
        const codeBooks: ApplicationDefinitionGroup = {
            name: "codebooks",
            applications: [],
            color: SharedColors.orange20,
        };

        if (UserInfoHelper.HasPacket(userInfo, ApplicationPacketEnum.Support)) {
            codeBooks.applications.push({
                name: "contacts",
                url: AppRoute.CODEBOOKS_CONTACTS,
                icon: "ContactCard",
            });

            codeBooks.applications.push({
                name: "companies",
                url: AppRoute.CODEBOOKS_COMPANIES,
                icon: "CityNext",
            });

            codeBooks.applications.push({
                name: "pantheonSubjects",
                url: AppRoute.CODEBOOKS_PANTHEON_SUBJECTS(),
                icon: "CityNext",
            });

            codeBooks.applications.push({
                name: "codebooks-projects",
                url: AppRoute.CODEBOOKS_PROJECTS,
                icon: "ProjectCollection",
            });

            codeBooks.applications.push(
                {
                    name: "wiki-entry-tags",
                    url: AppRoute.WIKI_ENTRY_TAGS_ADMIN,
                    icon: "Tag",
                },
                {
                    name: "wiki-entry-categories",
                    url: AppRoute.WIKI_ENTRY_CATEGORIES_ADMIN,
                    icon: "Sections",
                },
            );

            codeBooks.applications.push(
                {
                    name: "ibackup-entries",
                    url: AppRoute.IBACKUP_ENTRIES,
                    icon: "OfflineStorage",
                },
                {
                    name: "ibackup-computers",
                    url: AppRoute.IBACKUP_COMPUTERS,
                    icon: "PC1",
                },
            );
        }

        if (
            UserInfoHelper.HasPacket(
                userInfo,
                ApplicationPacketEnum.MarservisWorkOrders,
            ) &&
            UserInfoHelper.HasRole(OnixRoleEnum.TENANT, userInfo)
        ) {
            codeBooks.applications.push({
                name: "customers",
                url: AppRoute.CODEBOOKS_CUSTOMERS,
                icon: "ContactList",
            });

            codeBooks.applications.push({
                name: "items",
                url: AppRoute.CODEBOOKS_ITEMS,
                icon: "ProductList",
            });

            codeBooks.applications.push({
                name: "item_groups",
                url: AppRoute.CODEBOOKS_ITEM_GROUPS,
                icon: "AssessmentGroup",
            });

            codeBooks.applications.push({
                name: "materials",
                url: AppRoute.CODEBOOKS_MARSERVIS_MATERIALS,
                icon: "ProductVariant",
            });

            codeBooks.applications.push({
                name: "departments",
                url: AppRoute.CODEBOOKS_DEPARTMENTS,
                icon: "WorkforceManagement",
            });
        }

        if (
            UserInfoHelper.HasAnyPacket(
                userInfo,
                ApplicationPacketEnum.Support,
                ApplicationPacketEnum.MarservisWorkOrders,
            )
        ) {
        }

        if (UserInfoHelper.HasPacket(userInfo, ApplicationPacketEnum.Support)) {
            codeBooks.applications.push({
                name: "sql-backup-overview",
                url: AppRoute.SQL_BACKUP_OVERVIEW,
                icon: "Database",
            });
        }

        if (codeBooks.applications.length) groups.push(codeBooks);
    }
}

function makeWebDocumentGroups(
    userInfo: UserQuery | undefined,
    groups: ApplicationDefinitionGroup[],
    isOnixInfo: boolean,
) {
    const hasTmnDocumentTypes = !!userInfo?.user?.documentTypes?.find(
        (f) =>
            f.functionality === DocumentFunctionalityEnum.TmnDocumentsDirect ||
            f.functionality === DocumentFunctionalityEnum.TmnDocumentsWeb ||
            f.functionality ===
                DocumentFunctionalityEnum.TmnDocumentsClassificationQueue,
    );

    const hasWebDocumentAccess = UserInfoHelper.HasPacket(
        userInfo,
        ApplicationPacketEnum.WebDocuments,
    );
    const hasOnixPhoneDocumentAccess = UserInfoHelper.HasPacket(
        userInfo,
        ApplicationPacketEnum.OnixPhoneDocumentsOverview,
    );

    if (hasWebDocumentAccess || hasOnixPhoneDocumentAccess || hasTmnDocumentTypes) {
        // dokumenti
        const webDocuments: ApplicationDefinitionGroup = {
            name: "documents",
            applications: [],
        };

        webDocuments.applications = makeMainmenuGroupApplications(userInfo!);

        if (webDocuments.applications.length) groups.push(webDocuments);
    }
}

function makeUserAppsGroups(
    userInfo: UserQuery | undefined,
    groups: ApplicationDefinitionGroup[],
    isOnixInfo: boolean,
) {
    const userNavs: ApplicationDefinitionGroup = {
        name: "applications",
        applications: [],
        color: theme.palette.themePrimary,
    };

    if (
        UserInfoHelper.HasPacket(
            userInfo,
            ApplicationPacketEnum.OnixPhoneDocumentsOverview,
        )
    ) {
        userNavs.applications.push({
            name: "onix_phone_login",
            url: AppRoute.ONIX_PHONE_LOGIN_QRCODE,
            icon: "QRCode",
        });
    }

    if (DocumentService.HasWorkRecord(userInfo)) {
        userNavs.applications.push({
            name: "work_records",
            url: AppRoute.WORK_RECORDS(),
            icon: "Calendar",
        });
    }

    if (DocumentService.HasTravelWarrant(userInfo)) {
        userNavs.applications.push({
            name: "travel_warrants",
            url: AppRoute.TRAVEL_WARRANTS,
            icon: "Suitcase",
        });

        if (DocumentService.HasTravelWarrant2(userInfo)) {
            userNavs.applications.push({
                name: "travel_warrants_overview",
                url: AppRoute.TRAVEL_WARRANTS2_OVERVIEW,
                icon: "AnalyticsView",
            });
        }
    }

    if (
        UserInfoHelper.HasPacket(
            userInfo,
            ApplicationPacketEnum.MarservisWorkOrders,
        )
    ) {
        userNavs.applications.push({
            name: "work_orders",
            url: AppRoute.MARSERVIS_USER_WORK_ORDERS,
            icon: "Work",
        });
    }

    if (UserInfoHelper.HasPacket(userInfo, ApplicationPacketEnum.Support)) {
        userNavs.applications.push(
            {
                name: "support",
                url: AppRoute.SUPPORT,
                icon: "Phone",
                image: grunfImage// isOnixInfo ? grunfImage : undefined,
            },
            {
                name: "Korisnički Wiki",
                url: AppRoute.WIKI_ENTRY_ADMIN,
                icon: "Help",
            },
        );
    }

    if (userInfo?.user?.webAppRouting?.hasWebAppAccess) {
        // The desktop web client at /app (DMS, invoices, orders); a different application, full page load.
        userNavs.applications.push({
            name: "onix_web_app",
            url: AppRoute.WEB_APP,
            icon: "Website",
        });
    }

    if (userInfo?.user?.webAppRouting?.hasTickets) {
        // Customer ticket portal tickets in /app, while the portal shows its entry points (docs/decisions/0009).
        userNavs.applications.push({
            name: "portal_tickets_tile",
            url: PORTAL_HOME,
            icon: "Chat",
        });
    }

    if (userNavs.applications.length) groups.push(userNavs);
}

export const ApplicationListHelper = (
    userInfo?: UserQuery,
): ApplicationGroups => {
    const groups: ApplicationDefinitionGroup[] = [];
    const isSupportOnly = UserInfoHelper.HasRole(
        OnixRoleEnum.GRUNF_SUPPORT,
        userInfo,
    );
    const isOnixInfo =
        userInfo?.user?.companySettings?.pantheonCompanyName ===
        "ONIX INFORMATIKA d.o.o." ||
        userInfo?.user?.companySettings?.pantheonCompanyName === "Onix d.o.o." ||
        userInfo?.user?.companySettings?.pantheonCompanyName === "Block - Haley";

    if (isSupportOnly) {
        const supportOnlyNavs: ApplicationDefinitionGroup = {
            name: "applications",
            applications: [
                {
                    name: "support",
                    url: AppRoute.SUPPORT,
                    icon: "Phone",
                    image: grunfImage,
                },
                {
                    name: "Bob",
                    url: AppRoute.SUPPORT_BOB,
                    image: bob,
                },
                {
                    name: "onix-clr-installations",
                    url: AppRoute.ONIX_CLR_INSTALLATIONS,
                    icon: "Database",
                },
                {
                    name: "onixClrUsageReport",
                    url: AppRoute.ONIX_CLR_USAGE_REPORT,
                    icon: "BarChartVertical",
                },
                {
                    name: "onixDmsBillingReport",
                    url: AppRoute.ONIX_DMS_BILLING_REPORT,
                    icon: "ExcelDocument",
                },

                // Pantheon docs
                {
                    name: "pantheon_docs",
                    url: AppRoute.PANTHEON_DOCS,
                    icon: "TextDocument",
                },
                {
                    name: "Datalab mappings",
                    url: AppRoute.DATALAB_MAPPINGS,
                    icon: "Relationship",
                },
                {
                    name: "support-groups",
                    url: AppRoute.SUPPORT_GROUPS,
                    icon: "Group",
                },
                {
                    name: "support_absences",
                    url: AppRoute.SUPPORT_ABSENCES,
                    icon: "Calendar",
                },
            ],
            color: theme.palette.themePrimary,
        };

        groups.push(supportOnlyNavs);
        const res = new ApplicationGroups();
        res.groups = groups;
        return res;
    }

    makeUserAppsGroups(userInfo, groups, isOnixInfo);

    makeTenantGroups(userInfo, groups, isOnixInfo);

    makeWebDocumentGroups(userInfo, groups, isOnixInfo);

    makeAdminGroups(userInfo, groups, isOnixInfo);

    makeUserGroups(userInfo, groups, isOnixInfo);

    makeDeveloperGroups(userInfo, groups, isOnixInfo);

    const res = new ApplicationGroups();
    res.groups = groups;
    return res;
};

const ActiveUsersWrapper: FC<PropsWithChildren> = ({children}) => {
    return (
        <div style={{position: "relative"}}>
            {children}
        </div>
    );
};

export const makeMainmenuGroupApplications = (
    user: UserQuery,
): ApplicationDefinition[] => {
    const groups = makeMainMenuGroups(user);
    const applications: ApplicationDefinition[] = [];

    for (const group of groups) {
        // TEMP: hide TMN/DMS documents from the app list until the web implementation
        // matches the OnixPhone UX (confirmation/decline + file rendering).
        if (group.type === MainMenuGroupType.DMSDocuments) continue;

        applications.push({
            name: group.type as any,
            url: "url!",
            icon: group.icon,
            color: group.color,
            customElement: (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {group.items.map((item, index) => (
                        <WebDocumentGroupItem
                            key={group.type + "-" + item.endDoc().type}
                            item={item}
                            group={group}
                            index={index}
                        />
                    ))}
                </div>
            ),
        });
    }

    return applications;
};

interface IWebDocumentGroupItemProps {
    item: MainMenuItem;
    group: MainMenuGroup;
    index: number;
}

const WebDocumentGroupItem: FC<IWebDocumentGroupItemProps> = ({
                                                                  group,
                                                                  index,
                                                                  item,
                                                              }) => {
    const {t} = useTranslation();
    const {UserInfo} = useContext(AuthContext);
    const history = useNavigate();

    const docTypeCodesInTitles = useMemo(
        () => UserInfo?.user?.mobileSettings?.useDocumentTypeCodesInTitles,
        [UserInfo?.user?.mobileSettings?.useDocumentTypeCodesInTitles],
    );

    const endDocToString = useCallback(
        (doc: any): string | undefined => {
            // Match OnixPhone mainMenuItemToString: documentNote overrides acName
            let name: string | undefined = doc?.acName?.trim() || undefined;
            if (doc?.documentNote?.trim()) name = doc.documentNote.trim();

            if (name) {
                if (docTypeCodesInTitles === true && doc?.type) return doc.type;
                if (docTypeCodesInTitles === null || docTypeCodesInTitles === undefined) {
                    return doc?.type ? `${name} (${doc.type})` : name;
                }
                return name;
            }

            return t(doc?.functionality);
        },
        [docTypeCodesInTitles, t],
    );

    const docTypeToString = useCallback(
        (doc: any): string | undefined => {
            // Match OnixPhone docTypeToString: print title override > documentNote > acName
            let name: string | undefined =
                doc?.printDocumentTitleOverride?.trim() ||
                doc?.documentNote?.trim() ||
                doc?.acName?.trim() ||
                undefined;

            if (!name) name = t(doc?.functionality);

            if (docTypeCodesInTitles === true && doc?.type) return doc.type;
            if (docTypeCodesInTitles === null || docTypeCodesInTitles === undefined) {
                return doc?.type ? `${name} (${doc.type})` : name;
            }
            return name;
        },
        [docTypeCodesInTitles, t],
    );

    const linkStyle: HTMLAttributes<ILinkProps> = useMemo(
        () => ({
            width: 290,
            display: "flex",
            color: group.color,
            cursor: "pointer",
            background:
                "linear-gradient(130deg, " +
                Color(group.color).lighten(0.1) +
                ", " +
                Color(group.color).darken(0.1) +
                ")",
            borderRadius: "10px !important",
        }),
        [],
    );

    const rightDocName = useMemo(() => {
        const doc = item.endDoc();

        if (doc) {
            return endDocToString(doc);
        }
    }, [endDocToString, item]);

    const leftDocName = useMemo(() => {
        if (item.toType && !item.sameToDocument.length) {
            const doc = item.fromType;

            return docTypeToString(doc);
        }
    }, [docTypeToString, item]);

    const openWebDocumentItem = useCallback(() => {
        const endDoc = item.endDoc();
        switch (item.pairFunctionality) {
            case DocumentPairFunctionalityEnum.Copy:
                if (
                    endDoc.functionality ==
                    DocumentFunctionalityEnum.TmnDocumentsDirect ||
                    endDoc.functionality ==
                        DocumentFunctionalityEnum.TmnDocumentsWeb ||
                    endDoc.functionality ==
                        DocumentFunctionalityEnum.TmnDocumentsClassificationQueue
                ) {
                    history(AppRoute.WEB_DOCUMENT_TMN_LIST(endDoc.id));
                }
                break;
            case DocumentPairFunctionalityEnum.Picking:
                break;
            default:
                switch (item.endDoc().functionality) {
                    case DocumentFunctionalityEnum.TmnDocumentsDirect:
                    case DocumentFunctionalityEnum.TmnDocumentsWeb:
                    case DocumentFunctionalityEnum.TmnDocumentsClassificationQueue:
                        history(AppRoute.WEB_DOCUMENT_TMN_LIST(endDoc.id));
                        break;
                    case DocumentFunctionalityEnum.Orders:
                        // new document master detail
                        history(AppRoute.WEB_DOCUMENT_NEW_ORDER(endDoc.id));
                        break;
                    case DocumentFunctionalityEnum.DeliveryNotes:
                    case DocumentFunctionalityEnum.InvoiceDeliveryNotes:
                    case DocumentFunctionalityEnum.WarehouseReceipt:
                    case DocumentFunctionalityEnum.Inventory:
                        // new document master detail
                        history(AppRoute.WEB_DOCUMENT_NEW_MOVE(endDoc.id));
                        break;
                    case DocumentFunctionalityEnum.WorkOrdersReception:
                    case DocumentFunctionalityEnum.WorkOrdersOperative:
                    // work orders
                    case DocumentFunctionalityEnum.LurInvoice:
                    // lur invoice
                    case DocumentFunctionalityEnum.MarservisUserWorkOrders:
                    // marservis work orders
                    case DocumentFunctionalityEnum.FixedAssets:
                    // fixed assets
                    case DocumentFunctionalityEnum.WorkFlowTasks:
                    // work flow tasks
                    default:
                        // window.confirm(t("This functionality is not implemented yet."));
                        break;
                }
                break;
        }
    }, [item, history]);

    const big = useMemo(() => true, []);

    return (
        <div
            onClick={openWebDocumentItem}
            className="dashboard-item-link web-document-menu-item"
            style={{
                ...linkStyle,
                ...{
                    display: "flex",
                    flexDirection: "column",
                    padding: 10,
                    marginBottom: index < group.items.length - 1 ? 30 : 0,
                },
            }}
        >
            <div style={{flex: 1}}>
                <StackItem grow={1}>
                    <Stack
                        verticalAlign="end"
                        horizontal
                        horizontalAlign="space-between"
                        style={{marginBottom: 5, display: "flex"}}
                    >
                        <StackItem grow style={{flex: 1}}>
                            <Stack horizontal={!big} horizontalAlign="start">
                                {item.features
                                    .filter((f) => shownWebDocumentFeatures.includes(f))
                                    .map((f) => (
                                        <WebDocumentFeature
                                            big={big}
                                            key={`${f}-${group.type}-${item.endDoc().type}`}
                                            feature={f}
                                            item={item}
                                            group={group}
                                        />
                                    ))}
                            </Stack>
                        </StackItem>
                        <StackItem grow style={{flex: 1}}>
                            <Stack style={{padding: 10}} horizontalAlign="end">
                                <Icon iconName={group.icon} style={{color: "white"}}/>
                            </Stack>
                        </StackItem>
                    </Stack>
                </StackItem>
                <StackItem>
                    <Stack
                        style={{color: "white", width: "100%"}}
                        horizontal
                        horizontalAlign="space-evenly"
                        verticalAlign="center"
                    >
                        {leftDocName ? (
                            <Stack.Item align="start" style={{flex: 1}}>
                                {leftDocName}
                            </Stack.Item>
                        ) : (
                            <div/>
                        )}
                        {leftDocName && <Icon iconName="ChevronRight"/>}
                        {rightDocName ? (
                            <Stack.Item align="end" style={{flex: 1, textAlign: "right"}}>
                                {rightDocName}
                            </Stack.Item>
                        ) : (
                            <div/>
                        )}
                    </Stack>
                </StackItem>
            </div>
        </div>
    );
};

interface IWebDocumentFeatureProps {
    feature: MainMenuItemFeature;
    item: MainMenuItem;
    group: MainMenuGroup;
    big: boolean;
}

const WebDocumentFeature: FC<IWebDocumentFeatureProps> = ({
                                                              feature,
                                                              item,
                                                              group,
                                                              big,
                                                          }) => {
    const {t} = useTranslation();
    const [focused, setFocused] = useState(false);
    const history = useNavigate();

    const isMove = useMemo(() => {
        switch (item.endDoc().functionality) {
            case DocumentFunctionalityEnum.Orders:
                return false;
            default:
                return true;
        }
    }, [item]);

    const openWebDocumentItemList = useCallback(() => {
        const endDoc = item.endDoc();

        if (
            endDoc.functionality == DocumentFunctionalityEnum.TmnDocumentsDirect ||
            endDoc.functionality == DocumentFunctionalityEnum.TmnDocumentsWeb ||
            endDoc.functionality ==
                DocumentFunctionalityEnum.TmnDocumentsClassificationQueue
        ) {
            history(AppRoute.WEB_DOCUMENT_TMN_LIST(endDoc.id));
            return;
        }

        if (isMove) {
            history(AppRoute.WEB_DOCUMENT_MOVE_LIST(endDoc.id));
        } else {
            history(AppRoute.WEB_DOCUMENT_ORDER_LIST(endDoc.id));
        }
    }, [history, isMove, item]);

    const openWebDocumentItemFeature = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (event) event.stopPropagation();
            switch (feature) {
                case MainMenuItemFeature.Create:
                    break;
                case MainMenuItemFeature.DailyIncome:
                    break;
                case MainMenuItemFeature.List:
                    openWebDocumentItemList();
                    break;
                case MainMenuItemFeature.UnfinishedDocuments:
                    break;
                case MainMenuItemFeature.Warehouse:
                    break;
                default:
                    // window.confirm(t("This functionality is not implemented yet."));
                    break;
            }
        },
        [feature, openWebDocumentItemList],
    );

    return (
        <div
            onClick={openWebDocumentItemFeature}
            title={t(MainMenuItemFeature[feature])}
            style={{padding: 10, marginRight: 10, width: "100%"}}
            className="web-document-final-item"
            onMouseEnter={() => setFocused(true)}
            onMouseLeave={() => setFocused(false)}
        >
            <Stack horizontal verticalAlign="center">
        <span>
          <Icon
              iconName={getMainMenuGroupItemFeatureIcon(feature)}
              style={{color: "white", fontSize: theme.fonts.xLarge.fontSize}}
          />
        </span>
                <Stack
                    horizontal
                    horizontalAlign="space-between"
                    style={{width: "100%"}}
                    verticalAlign="center"
                >
                    <Text style={{color: "white", paddingLeft: 10}}>
                        {t(MainMenuItemFeature[feature])}
                    </Text>
                    <span>
            {/*<Icon*/}
                        {/*    iconName={"ChevronRight"}*/}
                        {/*    style={{color: "white", fontSize: theme.fonts.large.fontSize}}*/}
                        {/*/>*/}
          </span>
                </Stack>
            </Stack>
        </div>
    );
};
